/**
 * Complete Production-Grade Product Reviews & Moderation Route — Module 11.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { siteConfig } from "@/config/site";
import {
  Star,
  CheckCircle2,
  XCircle,
  MessageSquare,
  ShieldCheck,
  Check,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { DataTable, type ColumnDef } from "@/components/admin/common/data-table";
import {
  fetchAdminReviewsFromSupabase,
  updateAdminReviewStatusInSupabase,
  deleteAdminReviewFromSupabase,
  type ReviewRecord,
  type ReviewStatus,
} from "@/features/admin/reviews/store/admin-reviews-store";

export const Route = createFileRoute("/admin/reviews")({
  head: () => ({
    meta: [{ title: `Product Reviews — Admin — ${siteConfig.name}` }],
  }),
  component: AdminReviewsPage,
});

function AdminReviewsPage() {
  const [reviews, setReviews] = useState<ReviewRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadReviews = useCallback(async () => {
    try {
      const data = await fetchAdminReviewsFromSupabase();
      setReviews(data || []);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();

    const handleUpdate = () => {
      loadReviews();
    };

    window.addEventListener("reviews_updated", handleUpdate);
    window.addEventListener("storage", handleUpdate);

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("reviews_sync");
      bc.onmessage = () => {
        loadReviews();
      };
    } catch {
      // BroadcastChannel unavailable
    }

    return () => {
      window.removeEventListener("reviews_updated", handleUpdate);
      window.removeEventListener("storage", handleUpdate);
      bc?.close();
    };
  }, [loadReviews]);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleUpdateStatus = async (id: string, status: ReviewStatus, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = reviews.map((r) => (r.id === id ? { ...r, status } : r));
    setReviews(updated);
    const success = await updateAdminReviewStatusInSupabase(id, status);
    if (success) {
      triggerToast(
        `Review status updated to ${status === "PUBLISHED" ? "Approved & Published" : status}.`,
      );
    } else {
      triggerToast("Failed to update review status in database.");
      loadReviews();
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to delete this review?")) {
      const updated = reviews.filter((r) => r.id !== id);
      setReviews(updated);
      const success = await deleteAdminReviewFromSupabase(id);
      if (success) {
        triggerToast("Review deleted successfully.");
      } else {
        triggerToast("Failed to delete review from database.");
        loadReviews();
      }
    }
  };

  // Filtered List
  const filteredReviews = useMemo(() => {
    if (statusFilter === "ALL") return reviews;
    return reviews.filter((r) => r.status === statusFilter);
  }, [reviews, statusFilter]);

  // Statistics Summary
  const stats = useMemo(() => {
    const totalCount = reviews.length;
    const pendingCount = reviews.filter((r) => r.status === "PENDING_MODERATION").length;
    const publishedCount = reviews.filter((r) => r.status === "PUBLISHED").length;
    const sumRating = reviews.reduce((acc, r) => acc + r.rating, 0);
    const avgRating = totalCount > 0 ? (sumRating / totalCount).toFixed(1) : "5.0";

    return { totalCount, pendingCount, publishedCount, avgRating };
  }, [reviews]);

  // DataTable Columns
  const columns: ColumnDef<ReviewRecord>[] = [
    {
      id: "rating",
      header: "Rating",
      accessorKey: "rating",
      cell: (row) => (
        <div className="flex items-center gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-3.5 w-3.5 ${
                i < row.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
              }`}
            />
          ))}
        </div>
      ),
      sortable: true,
    },
    {
      id: "title",
      header: "Review Headline & Comment",
      cell: (row) => (
        <div>
          <p className="font-semibold text-foreground text-xs">{row.title}</p>
          <p className="text-[11px] text-muted-foreground line-clamp-2">"{row.comment}"</p>
        </div>
      ),
    },
    {
      id: "productName",
      header: "Product Item",
      accessorKey: "productName",
      cell: (row) => <span className="font-medium text-xs text-foreground">{row.productName}</span>,
      sortable: true,
    },
    {
      id: "customerName",
      header: "Reviewer",
      accessorKey: "customerName",
      cell: (row) => (
        <div>
          <p className="font-semibold text-foreground text-xs">{row.customerName}</p>
          {row.isVerifiedPurchase && (
            <span className="inline-block text-[9px] text-emerald-700 font-bold font-mono">
              ✓ Verified Buyer
            </span>
          )}
        </div>
      ),
      sortable: true,
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "status",
      cell: (row) => (
        <span
          className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
            row.status === "PUBLISHED"
              ? "bg-emerald-100 text-emerald-800"
              : row.status === "REJECTED"
                ? "bg-rose-100 text-rose-800"
                : "bg-amber-100 text-amber-800"
          }`}
        >
          {row.status}
        </span>
      ),
      sortable: true,
    },
    {
      id: "actions",
      header: "Moderation Actions",
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          {row.status !== "PUBLISHED" && (
            <button
              onClick={(e) => handleUpdateStatus(row.id, "PUBLISHED", e)}
              className="px-2 py-1 bg-emerald-700 text-white rounded text-[10px] font-bold hover:bg-emerald-800"
              title="Approve & Publish"
            >
              Approve
            </button>
          )}
          {row.status !== "REJECTED" && (
            <button
              onClick={(e) => handleUpdateStatus(row.id, "REJECTED", e)}
              className="px-2 py-1 border border-border text-muted-foreground hover:text-foreground rounded text-[10px] font-bold"
              title="Reject Review"
            >
              Reject
            </button>
          )}
          <button
            onClick={(e) => handleDelete(row.id, e)}
            className="p-1 text-muted-foreground hover:text-destructive transition-colors rounded"
            title="Delete Review"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-16">
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">Customer Feedback Operations</p>
          <h1 className="mt-1 font-display text-3xl text-foreground">
            Product Reviews & Moderation
          </h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Moderate incoming customer product reviews, verify purchase badges, and publish ratings
            to storefront.
          </p>
        </div>
        <div>
          <button
            onClick={() => loadReviews()}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md border border-border bg-card text-foreground hover:bg-accent transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Metric Cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Total Reviews Received
          </span>
          <span className="font-display text-xl font-bold text-foreground">{stats.totalCount}</span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Average Storefront Rating
          </span>
          <span className="font-display text-xl font-bold text-amber-700 flex items-center gap-1">
            {stats.avgRating} <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">
            Pending Moderation Queue
          </span>
          <span className="font-display text-xl font-bold text-amber-700">
            {stats.pendingCount}
          </span>
        </div>
        <div className="bg-card border border-border p-3.5 rounded-lg">
          <span className="eyebrow text-[10px] text-muted-foreground block">Published Reviews</span>
          <span className="font-display text-xl font-bold text-emerald-700">
            {stats.publishedCount}
          </span>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex items-center gap-1 border-b border-border pb-3 text-xs uppercase tracking-wider overflow-x-auto">
        {["ALL", "PENDING_MODERATION", "PUBLISHED", "REJECTED"].map((st) => (
          <button
            key={st}
            onClick={() => setStatusFilter(st)}
            className={`px-3 py-1.5 rounded transition-[color,background-color,border-color,box-shadow,transform,opacity] font-medium ${
              statusFilter === st
                ? "bg-primary text-primary-foreground font-semibold shadow-2xs"
                : "bg-card border border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {st} ({st === "ALL" ? reviews.length : reviews.filter((r) => r.status === st).length})
          </button>
        ))}
      </div>

      {/* Enterprise Data Table */}
      <DataTable
        data={filteredReviews}
        columns={columns}
        keyExtractor={(row) => row.id}
        searchPlaceholder="Search review title, comment, product, or customer..."
        searchField={(row) => `${row.title} ${row.comment} ${row.productName} ${row.customerName}`}
      />
    </div>
  );
}
